import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import useSWR from 'swr';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  User, Download, Search, Mail, Phone, Calendar, Loader2, 
  ChevronLeft, ChevronRight, Eye, Check, DollarSign, TrendingUp,
  FileText, ShoppingCart, UserCheck, RefreshCw, ChevronDown, ChevronUp,
  Clock, CreditCard, Users, MapPin, Globe, X, Printer, Shield,
  AlertTriangle, Smartphone, Monitor, RotateCcw, Flame, Sparkles, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import { invalidateFinancials } from '../hooks/useFinancials';
import { Badge } from './ui/badge';
import { BACKEND_URL } from '../config/api';
import TabPageLayout from './TabPageLayout';
import { ChipTabs } from './ui/chip-tabs';
import { ExportMenu } from './ExportMenu';
import { STATUS_COLORS, getStatusBadge as getStatusBadgeFromPalette, getAvatarBgColor, getAvatarTextColor } from '../config/statusColors';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

// Sanitize search input - remove special chars that could cause issues
const sanitizeSearchInput = (input) => {
  if (!input) return '';
  // Remove potentially dangerous characters, keep alphanumeric, spaces, @, ., -
  return input
    .replace(/[<>{}[\]\\\/^~`|]/g, '') // Remove dangerous chars
    .trim()
    .slice(0, 100); // Max 100 chars
};

// Expandable Contact Detail Panel
const ContactDetailPanel = ({ contact, onClose, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Pagination state for each tab
  const [bookingsPage, setBookingsPage] = useState(1);
  const [leadsPage, setLeadsPage] = useState(1);
  const [timelinePage, setTimelinePage] = useState(1);
  const [waiversPage, setWaiversPage] = useState(1);
  const [connectionsPage, setConnectionsPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  // Refetch function to reload contact details
  const refetchDetails = useCallback(async () => {
    try {
      const orgId = localStorage.getItem('organizationId');
      const response = await apiV2.get(`/organizations/${orgId}/relations/${encodeURIComponent(contact.id)}`);
      setDetails(response.data);
    } catch (error) {
      console.error('Error refetching details:', error);
    }
  }, [contact.id]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const orgId = localStorage.getItem('organizationId');
        const response = await apiV2.get(`/organizations/${orgId}/relations/${encodeURIComponent(contact.id)}`);
        setDetails(response.data);
      } catch (error) {
        console.error('Error fetching details:', error);
        if (error.response?.status === 404) {
          // Waiver-only contact with no bookings/leads — show minimal shell
          setDetails({
            contact: { name: contact.name, email: contact.email, phone: contact.phone },
            statistics: { totalBookings: 0, totalLeads: 0, totalSpent: 0, waiverCount: contact.waiverCount || 0 },
            bookings: [], leads: [], timeline: [], waivers: [], coParticipants: [], socialConnections: []
          });
        } else {
          toast.error('Failed to load contact details');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [contact.id]);

  // Update booking payment status
  const handleUpdatePaymentStatus = async (booking, newStatus) => {
    const bookingId = booking.bookingId || booking.id;
    if (!bookingId) {
      toast.error('Invalid booking');
      return;
    }
    
    try {
      const orgId = localStorage.getItem('organizationId');
      await apiV2.patch(`/organizations/${orgId}/bookings/${bookingId}`, {
        paymentStatus: newStatus
      });
      toast.success(`Payment status updated to ${newStatus}`);
      invalidateFinancials();
      // Refresh the contact details
      await refetchDetails();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const formatDate = (dateStr, timeStr = null) => {
    if (!dateStr) return '-';
    try {
      // Handle date-only strings (YYYY-MM-DD) to avoid timezone conversion issues
      // Parse as local date, not UTC
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('en-US', { 
          month: 'short', day: 'numeric', year: 'numeric',
          timeZoneName: 'short'
        });
      }
      // For full ISO timestamps, use regular parsing with timezone
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric', 
        hour: '2-digit', minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch { return '-'; }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      toast.info('Generating customer report...');
      
      const token = localStorage.getItem('jwtToken');
      const orgId = localStorage.getItem('organizationId');
      // Use v2 PostgreSQL endpoint
      const url = `${BACKEND_URL}/api/v2/organizations/${orgId}/relations/${encodeURIComponent(contact.id)}/report`;
      
      // Use XMLHttpRequest instead of fetch to bypass rrweb interceptor
      const blob = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.responseType = 'blob';
        
        xhr.onload = function() {
          if (xhr.status === 200) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Server returned ${xhr.status}: ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error occurred'));
        };
        
        xhr.ontimeout = function() {
          reject(new Error('Request timed out'));
        };
        
        xhr.timeout = 60000; // 60 second timeout for large reports
        xhr.send();
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `customer-report-${contact.name?.replace(/\s+/g, '-') || 'customer'}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Report downloaded!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.message || 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center">
        <div className="bg-white rounded-xl p-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading contact details...</p>
        </div>
      </div>,
      document.body
    );
  }

  // Currency formatting helper
  const formatCurrency = (amount, currency = 'USD') => {
    const currencySymbols = {
      'USD': '$',
      'AED': 'AED ',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
    };
    const symbol = currencySymbols[currency?.toUpperCase()] || currency + ' ';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };

  // Get primary currency from details
  const primaryCurrency = details?.summary?.currency || 'USD';

  // Helper function to paginate arrays
  const paginateData = (data, page, itemsPerPage) => {
    if (!data) return { items: [], totalPages: 0, currentPage: page };
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const start = (page - 1) * itemsPerPage;
    const items = data.slice(start, start + itemsPerPage);
    return { items, totalPages, currentPage: page, total: data.length };
  };

  // Get paginated data for each tab
  // Filter bookings to only show REAL bookings (paid, partial, admin-created) - NOT abandoned/widget checkouts
  const realBookings = (details?.bookings || []).filter(booking => {
    const isPaid = ['paid', 'partial', 'refunded', 'partially_refunded', 'completed'].includes(booking.paymentStatus);
    const isAdminCreated = ['admin', 'internal', 'direct'].includes(booking.source);
    const isConfirmed = booking.status === 'confirmed';
    // Only show if: paid/has payment OR admin-created OR confirmed
    return isPaid || isAdminCreated || isConfirmed;
  });
  const paginatedBookings = paginateData(realBookings, bookingsPage, ITEMS_PER_PAGE);
  const paginatedLeads = paginateData(details?.leads, leadsPage, ITEMS_PER_PAGE);
  const paginatedTimeline = paginateData(details?.timeline, timelinePage, ITEMS_PER_PAGE);
  const paginatedWaivers = paginateData(details?.waivers, waiversPage, ITEMS_PER_PAGE);
  const paginatedConnections = paginateData(details?.coParticipants, connectionsPage, ITEMS_PER_PAGE);

  // Pagination component
  const PaginationControls = ({ paginated, setPage }) => {
    if (paginated.totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Showing {((paginated.currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(paginated.currentPage * ITEMS_PER_PAGE, paginated.total)} of {paginated.total}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={paginated.currentPage === 1}
            className="p-1.5 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-600 px-2">
            {paginated.currentPage} / {paginated.totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(paginated.totalPages, p + 1))}
            disabled={paginated.currentPage === paginated.totalPages}
            className="p-1.5 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // Payment status helper
  const getPaymentStatusLabel = (status) => {
    if (status === 'paid') return { label: 'Payment Succeeded', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (status === 'payment_failed' || status === 'failed') return { label: 'Payment Failed', color: 'text-gray-500', bgColor: 'bg-gray-100' };
    if (status === 'pending') return { label: 'Payment Pending', color: 'text-amber-600', bgColor: 'bg-amber-100' };
    return { label: status || 'Unknown', color: 'text-gray-500', bgColor: 'bg-gray-100' };
  };

  const content = (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] overflow-y-auto py-4 sm:py-8 px-4 sm:px-0">
      <div data-testid="contact-detail-panel" className="bg-white w-full max-w-full sm:max-w-4xl mx-auto shadow-2xl flex flex-col rounded-lg sm:rounded-2xl overflow-hidden print:max-w-none print:max-h-none print:overflow-visible print:rounded-none">
        {/* Header - Mobile responsive */}
        <div className="bg-white border-b border-gray-100 p-4 sm:p-6 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
                {(contact.name || 'U')[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{contact.name}</h2>
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-3 mt-1 text-xs sm:text-sm text-gray-500">
                  {contact.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 flex-shrink-0" /><span className="truncate">{contact.email}</span></span>}
                  {contact.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 flex-shrink-0" />{contact.phone}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 print:hidden flex-shrink-0">
              <Button 
                onClick={handleGenerateReport}
                disabled={generatingReport}
                data-testid="contact-generate-report-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm"
                size="sm"
              >
                {generatingReport ? (
                  <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                )}
                <span className="hidden sm:inline">Generate Report</span>
                <span className="sm:hidden">Report</span>
              </Button>
              <button onClick={onClose} data-testid="contact-detail-close" className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Summary Stats - Mobile responsive */}
          {details?.statistics && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mt-4 sm:mt-6">
              <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
                <div className="text-xl sm:text-3xl font-light tracking-tight text-gray-900">{formatCurrency(details.statistics.totalSpent || 0, primaryCurrency)}</div>
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">Total Spent</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
                <div className="text-xl sm:text-3xl font-light tracking-tight text-gray-900">{details.statistics.totalBookings || 0}</div>
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">Bookings</div>
              </div>
              {details.statistics.totalLeads > 0 && (
                <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
                  <div className="text-xl sm:text-3xl font-light tracking-tight text-blue-600">{details.statistics.totalLeads}</div>
                  <div className="text-[10px] sm:text-xs text-gray-500 mt-1">Lead Inquiries</div>
                </div>
              )}
              <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
                <div className="text-xl sm:text-3xl font-light tracking-tight text-gray-900">{details.statistics.waiverCount || 0}</div>
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">Waivers Signed</div>
              </div>
            </div>
          )}
          
          {/* Also Known As - Shows alternate names */}
          {details?.contact?.alsoKnownAs && details.contact.alsoKnownAs.length > 0 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Also Known As
              </h4>
              <div className="flex flex-wrap gap-2 text-xs">
                {details.contact.alsoKnownAs.map((name, i) => (
                  <span key={i} className="bg-white border border-amber-100 rounded px-2 py-1 text-gray-700">{name}</span>
                ))}
              </div>
            </div>
          )}
          
          {/* Identity Audit - Shows all name/email/phone variations */}
          {details?.identityAudit && (details.identityAudit.hasMultipleNames || details.identityAudit.hasMultipleEmails || details.identityAudit.hasMultiplePhones) && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Identity Variations Detected
              </h4>
              <div className="flex flex-wrap gap-2 text-xs">
                {details.identityAudit.hasMultipleNames && (
                  <div className="bg-white border border-amber-100 rounded px-2 py-1">
                    <span className="text-gray-500">Names: </span>
                    <span className="text-gray-700">{details.identityAudit.names.join(', ')}</span>
                  </div>
                )}
                {details.identityAudit.hasMultipleEmails && (
                  <div className="bg-white border border-amber-100 rounded px-2 py-1">
                    <span className="text-gray-500">Emails: </span>
                    <span className="text-gray-700">{details.identityAudit.emails.join(', ')}</span>
                  </div>
                )}
                {details.identityAudit.hasMultiplePhones && (
                  <div className="bg-white border border-amber-100 rounded px-2 py-1">
                    <span className="text-gray-500">Phones: </span>
                    <span className="text-gray-700">{details.identityAudit.phones.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tabs - Mobile responsive with horizontal scroll */}
        <div className="border-b border-gray-200 px-3 sm:px-6 flex-shrink-0 print:hidden overflow-x-auto">
          <div className="flex gap-0 sm:gap-1 -mb-px min-w-max">
            {[
              { id: 'timeline', label: 'Timeline', icon: Clock },
              { id: 'bookings', label: 'Bookings', icon: Calendar },
              { id: 'waivers', label: 'Waivers', icon: Shield },
              { id: 'connections', label: 'Connections', icon: Users }
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                data-testid={`contact-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 print:overflow-visible">
          {/* Timeline Tab - Enhanced with payment status */}
          {activeTab === 'timeline' && (
            <div className="space-y-4">
              <h3 className="dash-section-title mb-4">Activity Timeline</h3>

              {/* Trip participation cards — for waiver-only contacts */}
              {details?.tripParticipation?.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Trips as Guest</p>
                  {details.tripParticipation.map((trip, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{trip.serviceName}</p>
                        <p className="text-xs text-gray-500">{trip.date}{trip.startTime ? ` at ${trip.startTime}` : ''} · #{trip.confirmationCode}</p>
                        <p className="text-xs text-gray-400">Booking holder: {trip.customerName} · {trip.partySize} guest{trip.partySize !== 1 ? 's' : ''}</p>
                      </div>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Guest</span>
                    </div>
                  ))}
                </div>
              )}
              {details?.timeline?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No activity recorded</p>
              ) : (
                <>
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    {paginatedTimeline.items?.map((event, idx) => {
                      const paymentInfo = event.type === 'booking_created' ? getPaymentStatusLabel(event.paymentStatus) : null;
                      
                      // Determine icon and color based on event type
                      const getEventStyle = (type) => {
                        switch(type) {
                          case 'booking_created':
                            return { bg: 'bg-blue-500', icon: <Calendar className="w-3 h-3 text-white" /> };
                          case 'checked_in':
                            return { bg: 'bg-green-500', icon: <Check className="w-3 h-3 text-white" /> };
                          case 'waiver_signed':
                            return { bg: 'bg-purple-500', icon: <FileText className="w-3 h-3 text-white" /> };
                          case 'lead_captured':
                            return { bg: 'bg-orange-500', icon: <Flame className="w-3 h-3 text-white" /> };
                          case 'checkout_reached':
                            return { bg: 'bg-amber-500', icon: <ShoppingCart className="w-3 h-3 text-white" /> };
                          default:
                            return { bg: 'bg-gray-400', icon: <Clock className="w-3 h-3 text-white" /> };
                        }
                      };
                      
                      const eventStyle = getEventStyle(event.type);
                      
                      return (
                        <div key={idx} className="relative pl-10 pb-6 last:pb-0 flex items-start">
                          <div className={`absolute left-2 top-0 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${eventStyle.bg}`}>
                            {eventStyle.icon}
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm sm:text-base">
                                  {event.icon && <span className="mr-1">{event.icon}</span>}
                                  {event.description}
                                </p>
                                {event.type === 'lead_captured' && event.message && (
                                  <p className="text-xs sm:text-sm text-gray-600 mt-1.5 bg-white border border-gray-100 rounded-lg px-3 py-2 leading-relaxed" data-testid="timeline-lead-message">
                                    &ldquo;{event.message}&rdquo;
                                  </p>
                                )}
                                {event.type === 'booking_created' && event.amount > 0 && (
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <span className={`text-sm font-semibold ${paymentInfo?.color || 'text-gray-600'}`}>
                                      {formatCurrency(event.amount, event.currency || primaryCurrency)}
                                    </span>
                                    {paymentInfo && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${paymentInfo.bgColor} ${paymentInfo.color}`}>
                                        {paymentInfo.label}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {event.type === 'lead_captured' && event.preferredDate && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Interested for: {formatDate(event.preferredDate)}
                                  </p>
                                )}
                                {event.confirmationCode && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Confirmation: {event.confirmationCode}
                                  </p>
                                )}
                              </div>
                              <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(event.date)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <PaginationControls paginated={paginatedTimeline} setPage={setTimelinePage} />
                </>
              )}
            </div>
          )}

          {/* Bookings Tab - Clean layout with proper colors */}
          {activeTab === 'bookings' && (
            <div className="space-y-4">
              <h3 className="dash-section-title mb-4">All Bookings ({realBookings.length})</h3>
              {realBookings.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No bookings found</p>
              ) : (
                <>
                  <div className="space-y-2">
                    {paginatedBookings.items?.map((booking, idx) => {
                      const isPaid = booking.paymentStatus === 'paid';
                      const isRefunded = booking.paymentStatus === 'refunded' || booking.paymentStatus === 'partially_refunded';
                      const bookingDate = booking.date || booking.bookingDate;
                      const bookingTime = booking.time || booking.startTime;
                      
                      // Generate magic link for ticket
                      const handleViewTicket = async () => {
                        try {
                          const bookingId = booking.confirmationCode || booking.bookingId || booking.id;
                          const response = await apiV2.post(`/public/bookings/${bookingId}/generate-magic-link`);
                          const magicLink = response.data.magicLink;
                          window.open(magicLink, '_blank');
                        } catch (error) {
                          console.error("Error generating magic link:", error);
                          toast.error("Failed to open ticket");
                        }
                      };
                      
                      return (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4">
                          {/* Top row: Service name + Amount */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900">
                                {booking.serviceName || 'Booking'}
                                {booking.status === 'cancelled' && (
                                  <span className="ml-2 text-xs text-red-500">Cancelled</span>
                                )}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {bookingDate ? formatDate(bookingDate) : 'No date'}{bookingTime ? ` • ${bookingTime}` : ''}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {booking.guests || booking.partySize || 1} guest{(booking.guests || booking.partySize || 1) > 1 ? 's' : ''} • {booking.confirmationCode || booking.bookingId?.slice(0, 8) || booking.id?.slice(0, 8)}
                              </p>
                            </div>
                            
                            {/* Amount - Blue if paid, Grey if refunded */}
                            <div className="text-right">
                              <p className={`text-lg font-semibold ${isRefunded ? 'text-gray-400' : 'text-blue-600'}`}>
                                {formatCurrency(booking.amount || booking.totalAmount || 0, booking.currency || primaryCurrency)}
                              </p>
                            </div>
                          </div>
                          
                          {/* Bottom row: Actions - Payment Status Dropdown + View Ticket */}
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                            {/* Payment Status Dropdown */}
                            <div className="flex items-center gap-2">
                              <Select
                                value={booking.paymentStatus || 'unpaid'}
                                onValueChange={(val) => handleUpdatePaymentStatus(booking, val)}
                              >
                                <SelectTrigger
                                  data-testid={`booking-payment-status-trigger-${booking.bookingId || booking.id}`}
                                  className={`w-32 h-8 text-xs font-medium ${
                                  booking.paymentStatus === 'paid' ? 'bg-green-50 border-green-200 text-green-700' :
                                  booking.paymentStatus === 'refunded' ? 'bg-gray-50 border-gray-200 text-gray-600' :
                                  booking.paymentStatus === 'partially_refunded' ? 'bg-orange-50 border-orange-200 text-orange-700' :
                                  booking.paymentStatus === 'partial' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                                  'bg-red-50 border-red-200 text-red-700'
                                }`}>
                                  <div className="flex items-center gap-1.5">
                                    <span className={`w-2 h-2 rounded-full ${
                                      booking.paymentStatus === 'paid' ? 'bg-green-500' :
                                      booking.paymentStatus === 'refunded' ? 'bg-gray-400' :
                                      booking.paymentStatus === 'partially_refunded' ? 'bg-orange-500' :
                                      booking.paymentStatus === 'partial' ? 'bg-yellow-500' :
                                      'bg-red-500'
                                    }`} />
                                    <SelectValue />
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem data-testid="payment-status-option-unpaid" value="unpaid">Unpaid</SelectItem>
                                  <SelectItem data-testid="payment-status-option-pending" value="pending">Pending</SelectItem>
                                  <SelectItem data-testid="payment-status-option-partial" value="partial">Partial</SelectItem>
                                  <SelectItem data-testid="payment-status-option-paid" value="paid">Paid</SelectItem>
                                  <SelectItem data-testid="payment-status-option-refunded" value="refunded">Refunded</SelectItem>
                                  <SelectItem data-testid="payment-status-option-partially_refunded" value="partially_refunded">Partial Refund</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {/* Stripe Link - only show if paid or partial */}
                              {(booking.paymentStatus === 'paid' || booking.paymentStatus === 'partial') && (
                                <button
                                  onClick={() => {
                                    window.open('https://dashboard.stripe.com/payments', '_blank');
                                  }}
                                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                                >
                                  Stripe →
                                </button>
                              )}
                            </div>
                            
                            {/* View Ticket Button */}
                            <button
                              onClick={handleViewTicket}
                              data-testid={`contact-view-ticket-${idx}`}
                              className="text-xs px-3 py-1.5 border border-blue-300 text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                            >
                              View Ticket
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <PaginationControls paginated={paginatedBookings} setPage={setBookingsPage} />
                </>
              )}
            </div>
          )}

          {/* Waivers Tab - Full legal details, flat design */}
          {activeTab === 'waivers' && (
            <div className="space-y-4">
              <h3 className="dash-section-title mb-4">Signed Waivers ({details?.waivers?.length || 0})</h3>
              {details?.waivers?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No waivers signed</p>
              ) : (
                <>
                  <div className="space-y-3">
                    {paginatedWaivers.items?.map((waiver, idx) => {
                      const deviceInfo = waiver.deviceInfo || {};
                      
                      // Get signature source
                      const getSignatureSource = () => {
                        if (waiver.signatureData && waiver.signatureData.startsWith('data:')) return waiver.signatureData;
                        const sigUrl = waiver.signatureUrl || waiver.signatureImage;
                        if (!sigUrl) return null;
                        if (sigUrl.startsWith('data:') || sigUrl.startsWith('http')) return sigUrl;
                        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
                        return `${apiBase}${sigUrl}`;
                      };
                      const signatureSrc = getSignatureSource();
                      
                      return (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4">
                          {/* Header row */}
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-medium text-gray-900">{waiver.serviceName || 'Waiver'}</h4>
                              <p className="text-sm text-gray-600">{waiver.bookingDate ? formatDate(waiver.bookingDate) : '-'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-green-600">Signed {waiver.signedAt ? formatDate(waiver.signedAt) : ''}</p>
                              <p className="text-xs text-gray-500 mt-0.5">v{waiver.waiverVersion || waiver.version || waiver.templateVersion || '1.0'}</p>
                            </div>
                          </div>
                          
                          {/* Basic Signer Info - Always visible */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-xs text-gray-400">Name</p>
                              <p className="text-gray-900">{waiver.signatureName || waiver.name || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">Email</p>
                              <p className="text-gray-900">{waiver.email || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">Phone</p>
                              <p className="text-gray-900">{waiver.phoneNumber || waiver.phone || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">DOB</p>
                              <p className="text-gray-900">{waiver.dateOfBirth ? formatDate(waiver.dateOfBirth) : '-'}</p>
                            </div>
                          </div>
                          
                          {/* Signature - Always visible */}
                          {signatureSrc && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="bg-white rounded border border-gray-200 p-2 inline-block">
                                <img 
                                  src={signatureSrc} 
                                  alt="Signature" 
                                  className="h-10 max-w-[120px] object-contain" 
                                  onError={(e) => { e.target.style.display = 'none'; }} 
                                />
                              </div>
                            </div>
                          )}
                          
                          {/* Collapsible Details */}
                          <details className="mt-3">
                            <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-700">Show details</summary>
                            <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                              {/* IDs */}
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <p className="text-gray-400">Waiver ID</p>
                                  <p className="font-mono text-gray-600">{waiver.id || waiver._id || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-400">Confirmation</p>
                                  <p className="font-mono text-gray-600">{waiver.confirmationCode || waiver.bookingConfirmationCode || '-'}</p>
                                </div>
                              </div>
                              
                              {/* Technical Verification */}
                              <div>
                                <p className="text-xs text-gray-400 mb-2">Technical Verification</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                  <div>
                                    <p className="text-gray-400">IP</p>
                                    <p className="font-mono text-gray-600">{waiver.ipAddress || '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400">Platform</p>
                                    <p className="text-gray-600">{deviceInfo.platform || deviceInfo.os || '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400">Browser</p>
                                    <p className="text-gray-600">{deviceInfo.browser || '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400">Device</p>
                                    <p className="text-gray-600">{deviceInfo.deviceType || deviceInfo.device || (deviceInfo.isMobile ? 'Mobile' : 'Desktop') || '-'}</p>
                                  </div>
                                </div>
                              </div>
                              
                              {/* User Agent */}
                              {(deviceInfo.userAgent || waiver.userAgent) && (
                                <div>
                                  <p className="text-gray-400 text-xs">User Agent</p>
                                  <p className="font-mono text-xs text-gray-500 break-all">{deviceInfo.userAgent || waiver.userAgent}</p>
                                </div>
                              )}
                              
                              {/* Emergency Contact */}
                              {(waiver.emergencyContact || waiver.emergencyName) && (
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                  <div>
                                    <p className="text-gray-400">Emergency Contact</p>
                                    <p className="text-gray-900">{waiver.emergencyContact || waiver.emergencyName || '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400">Emergency Phone</p>
                                    <p className="text-gray-900">{waiver.emergencyPhone || '-'}</p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Medical/Notes */}
                              {(waiver.medicalConditions || waiver.notes || waiver.specialRequests) && (
                                <div className="text-xs">
                                  <p className="text-gray-400 mb-1">Medical/Notes</p>
                                  <p className="text-gray-700">{waiver.medicalConditions || waiver.notes || waiver.specialRequests}</p>
                                </div>
                              )}
                              
                              {/* Agreements */}
                              {(waiver.agreedToTerms || waiver.agreedToRelease || waiver.agreedToPhoto) && (
                                <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                                  {waiver.agreedToTerms && <span>✓ Terms</span>}
                                  {waiver.agreedToRelease && <span>✓ Liability Release</span>}
                                  {waiver.agreedToPhoto && <span>✓ Photo Release</span>}
                                </div>
                              )}
                            </div>
                          </details>
                        </div>
                      );
                    })}
                  </div>
                  <PaginationControls paginated={paginatedWaivers} setPage={setWaiversPage} />
                </>
              )}
            </div>
          )}

          {/* Connections Tab */}
          {activeTab === 'connections' && (
            <div className="space-y-4">
              <h3 className="dash-section-title mb-4">Connected People ({details?.coParticipants?.length || 0})</h3>
              <p className="text-sm text-gray-500 mb-4">
                People who have been on trips with this contact (waiver co-signers, group members, etc.)
              </p>
              {details?.coParticipants?.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">No co-signers found on shared trips</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {paginatedConnections.items?.map((person, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-sm font-medium text-blue-600">
                          {(person.name || 'U')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{person.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                            {person.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{person.phone}</span>}
                            {person.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{person.email}</span>}
                            {person.trips?.length > 0 && (
                              <span className="flex items-center gap-1 text-blue-500">
                                <Calendar className="w-3 h-3" />{person.trips.length} trip{person.trips.length !== 1 ? 's' : ''} together
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <PaginationControls paginated={paginatedConnections} setPage={setConnectionsPage} />
                </>
              )}
            </div>
          )}
        </div>

        {/* Print-only: All data */}
        <div className="hidden print:block p-6 space-y-8">
          <h3 className="font-bold text-lg border-b pb-2">Complete Contact Record</h3>
          <p className="text-xs text-gray-500">Generated: {new Date().toLocaleString()}</p>
          
          {/* All bookings for print */}
          <div>
            <h4 className="font-semibold mb-2">All Bookings ({details?.bookings?.length || 0})</h4>
            {details?.bookings?.map((b, i) => (
              <div key={i} className="border-b py-2 text-sm">
                <p><strong>{b.serviceName}</strong> - {formatDate(b.date)}</p>
                <p>Amount: {formatCurrency(b.amount, b.currency || primaryCurrency)} | Status: {b.status} | Payment: {b.paymentStatus}</p>
                {b.stripePaymentIntentId && <p className="text-xs">Payment ID: {b.stripePaymentIntentId}</p>}
              </div>
            ))}
          </div>
          
          {/* All waivers for print */}
          <div>
            <h4 className="font-semibold mb-2">All Waivers ({details?.waivers?.length || 0})</h4>
            {details?.waivers?.map((w, i) => (
              <div key={i} className="border-b py-2 text-sm">
                <p><strong>{w.serviceName}</strong></p>
                <p>Signer: {w.signatureName} | Phone: {w.phoneNumber}</p>
                <p>Signed: {formatDate(w.signedAt)} | IP: {w.ipAddress}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render outside of React component hierarchy  
  return ReactDOM.createPortal(content, document.body);
};

const RelationsTab = ({ initialFilter = 'all', onNavigate }) => {
  // Filters - use initialFilter prop
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialFilter);
  const [sortBy, setSortBy] = useState('recent');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const limit = 20;
  
  // Export
  const [exporting, setExporting] = useState(false);
  
  // Detail panel
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Copy to clipboard animation state
  const [copiedItems, setCopiedItems] = useState({});
  
  // Status dropdown state
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);

  // Build SWR key for data fetching - v2 API uses org_id in path
  const orgId = localStorage.getItem('organizationId');
  const swrKey = useMemo(() => {
    if (!orgId) return null;
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: searchQuery,
      status: statusFilter,
      sort_by: sortBy,
      sort_dir: sortDir
    });
    return `/organizations/${orgId}/relations?${params}`;
  }, [page, searchQuery, statusFilter, sortBy, sortDir, orgId]);

  // Fetch data with useSWR - auto-revalidates on focus
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    swrKey,
    async (url) => {
      const response = await apiV2.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      keepPreviousData: true,
    }
  );

  // Extract data from SWR response - v2 API returns {contacts: [...]}
  const contacts = data?.contacts || data?.data || [];
  const stats = data?.stats || { total: data?.total || 0, customers: 0, leads: 0, waivers: 0, returning: 0, ghost: 0, customRequests: 0 };
  const pagination = data?.pagination || { page: data?.page || 1, limit: data?.limit || 20, total: data?.total || 0, totalPages: Math.ceil((data?.total || 0) / (data?.limit || 20)) };
  const loading = isLoading;
  const isInitialLoading = isLoading && !data;

  // Currency formatting helper (also used in list)
  const formatCurrency = (amount, currency = 'USD') => {
    const currencySymbols = {
      'USD': '$',
      'AED': 'AED ',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
    };
    const symbol = currencySymbols[currency?.toUpperCase()] || currency + ' ';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };
  
  // Copy to clipboard with animation
  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
    setCopiedItems(prev => ({ ...prev, [key]: true }));
    setTimeout(() => {
      setCopiedItems(prev => ({ ...prev, [key]: false }));
    }, 3000);
  };
  
  // Update contact status — use apiV2 (axios) to avoid the rrweb fetch-recorder
  // "Response body already used" clone error that made the raw fetch show a
  // misleading success toast (and fire it twice) even on a non-2xx response.
  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await apiV2.put(`/organizations/${orgId}/relations/${encodeURIComponent(contactId)}/status`, {
        status: newStatus
      });
      // Revalidate data via SWR
      mutate();
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to update status');
    } finally {
      setStatusDropdownOpen(null);
    }
  };

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (statusDropdownOpen) setStatusDropdownOpen(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [statusDropdownOpen]);

  // Debounce search (300ms) with sanitization
  useEffect(() => {
    const timer = setTimeout(() => {
      const sanitized = sanitizeSearchInput(searchInput);
      setSearchQuery(sanitized);
      setPage(1); // Reset to page 1 on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Lead actions - now use mutate() to refresh data
  const markLeadAsViewed = async (contact, e) => {
    e.stopPropagation();
    if (!contact.leadId) return;
    try {
      await apiV2.patch(`/leads/${contact.leadId}`, { 
        viewedBy: 'admin', 
        viewedAt: new Date().toISOString() 
      });
      mutate();
      toast.success('Marked as viewed');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const markLeadAsContacted = async (contact, e) => {
    e.stopPropagation();
    if (!contact.leadId) return;
    try {
      await apiV2.patch(`/leads/${contact.leadId}`, { 
        status: 'contacted', 
        contactedAt: new Date().toISOString() 
      });
      mutate();
      toast.success('Marked as contacted');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const convertLead = async (contact, e) => {
    e.stopPropagation();
    if (!contact.leadId) return;
    if (!confirm('Mark as converted?')) return;
    try {
      await apiV2.patch(`/leads/${contact.leadId}`, { 
        status: 'converted', 
        convertedAt: new Date().toISOString() 
      });
      mutate();
      toast.success('Lead converted!');
    } catch (error) {
      toast.error('Failed to convert');
    }
  };

  // Export - accepts format from ExportMenu
  const handleExport = async (format = 'csv') => {
    try {
      setExporting(true);
      const exportOrgId = localStorage.getItem('organizationId');
      const response = await apiV2.get(`/organizations/${exportOrgId}/relations/export?status=${statusFilter}&format=${format}`, {
        responseType: 'blob'
      });
      
      const contentType = format === 'json' ? 'application/json' : 'text/csv';
      const extension = format === 'json' ? 'json' : 'csv';
      
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relations-${new Date().toISOString().split('T')[0]}.${extension}`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Get status badge config - uses unified color palette
  const getStatusBadge = (contact) => {
    return getStatusBadgeFromPalette(contact);
  };
  
  // Check if contact is a returning customer (has made previous purchases and has recent lead activity)
  const isReturningLead = (contact) => {
    return contact.isReturningCustomer && (contact.status === 'lead' || contact.status === 'recent_lead');
  };

  return (
    <TabPageLayout
      title="Relations"
      subtitle="Click any contact to view full history, waivers, and connected people"
      loading={isInitialLoading}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => mutate()}
            data-testid="relations-refresh-btn"
            disabled={isValidating}
            aria-label="Refresh contacts"
            className="h-10 w-10 p-0 bg-white text-gray-700 shadow-[0_8px_24px_rgba(0,0,0,0.04)] hover:bg-white hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-shadow"
          >
            <RefreshCw className={`w-4 h-4 ${isValidating ? 'animate-spin' : ''}`} />
          </Button>
          <ExportMenu
            onExport={handleExport}
            formats={['csv', 'json']}
            disabled={exporting || stats.total === 0}
          />
        </div>
      }
    >
      <div className="space-y-6">
        {/* Status Filter Pills with live counts — shared ChipTabs component */}
        <div data-testid="relations-filter-chips">
          <ChipTabs
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            testIdPrefix="relations-filter"
            size="lg"
            tabs={[
              { value: 'all', label: 'All', count: stats.total },
              { value: 'customer', label: 'Customers', count: stats.customers },
              { value: 'lead', label: 'Leads', count: stats.leads },
              { value: 'custom_request', label: 'Custom', count: stats.customRequests },
              { value: 'returning', label: 'Returning', count: stats.returning },
              { value: 'ghost', label: 'Ghost', count: stats.ghost },
              { value: 'waiver', label: 'Waivers', count: stats.waivers },
            ]}
          />
        </div>

      {/* Search & Sort Bar */}
      <div className="card-soft p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <label htmlFor="contact-search" className="sr-only">Search contacts</label>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              id="contact-search"
              data-testid="relations-search-input"
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 h-10 bg-gray-50 border-gray-200 focus:bg-white"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                data-testid="relations-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 hidden sm:inline">Sort:</span>
            {[
              { key: 'recent', label: 'Recent' },
              { key: 'name', label: 'Name' },
              { key: 'bookings', label: 'Bookings' },
              { key: 'revenue', label: 'Revenue' }
            ].map(sort => (
              <button
                key={sort.key}
                data-testid={`relations-sort-${sort.key}`}
                onClick={() => {
                  if (sortBy === sort.key) {
                    setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(sort.key);
                    setSortDir(sort.key === 'name' ? 'asc' : 'desc');
                  }
                  setPage(1);
                }}
                className={`px-3.5 py-1.5 h-9 text-xs rounded-full font-medium transition-all shadow-[0_8px_24px_rgba(0,0,0,0.04)] ${
                  sortBy === sort.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {sort.label}
                {sortBy === sort.key && (
                  <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="card-soft overflow-hidden">
        {contacts.length === 0 ? (
          <div className="text-center py-20" data-testid="relations-empty-state">
            <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="dash-section-title text-base mb-1">
              {searchQuery || statusFilter !== 'all' ? 'No contacts found' : 'No contacts yet'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Contacts will appear after bookings, leads, or waivers'}
            </p>
            {(searchQuery || statusFilter !== 'all') && (
              <Button
                variant="outline"
                size="sm"
                data-testid="relations-clear-filters-btn"
                onClick={() => {
                  setSearchInput('');
                  setStatusFilter('all');
                  setPage(1);
                }}
                className="h-9"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className={`overflow-x-auto transition-opacity duration-200 ${isValidating && !isInitialLoading ? 'opacity-60 pointer-events-none' : 'opacity-100'}`} data-testid="relations-table">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Name</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Email</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Phone</th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Bookings</th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Waivers</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Revenue</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 hidden xl:table-cell">Joined</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.map((contact, idx) => {
                  const statusBadge = getStatusBadge(contact);
                  const isNewLead = contact.status === 'lead' && !contact.leadViewedBy;
                  
                  // Get first activity date (joined date)
                  const joinedDate = contact.firstSeen || contact.createdAt || contact.leadCreatedAt;
                  const formattedJoined = joinedDate ? new Date(joinedDate).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  }) : '—';
                  
                  return (
                    <tr 
                      key={contact.id || idx} 
                      data-testid={`relations-row-${contact.id || idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedContact(contact)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedContact(contact); } }}
                      className="hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none focus:bg-blue-50"
                    >
                      {/* Name */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${getAvatarBgColor(contact.status)}`}>
                            <span className={`text-sm font-medium ${getAvatarTextColor(contact.status)}`}>
                              {(contact.name || 'U')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-gray-900 truncate max-w-[150px]">{contact.name || 'Unknown'}</span>
                            {/* Show alternate names if exists */}
                            {contact.alsoKnownAs && contact.alsoKnownAs.length > 0 && (
                              <span className="text-xs text-gray-400 truncate max-w-[150px]" title={`Also: ${contact.alsoKnownAs.join(', ')}`}>
                                Also: {contact.alsoKnownAs.slice(0, 2).join(', ')}{contact.alsoKnownAs.length > 2 ? '...' : ''}
                              </span>
                            )}
                            {/* Show lead count if multiple lead submissions */}
                            {contact.leadCount > 1 && (
                              <span className="text-xs text-amber-600 font-medium">
                                {contact.leadCount} inquiries
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      {/* Status - Dropdown selector */}
                      <td className="px-4 py-4">
                        <div className="relative">
                          <button
                            data-testid={`relations-status-trigger-${contact.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setStatusDropdownOpen(statusDropdownOpen === contact.id ? null : contact.id);
                            }}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border whitespace-nowrap transition-colors ${statusBadge.className} hover:opacity-80`}
                          >
                            {statusBadge.label}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          
                          {/* Dropdown menu */}
                          {statusDropdownOpen === contact.id && (
                            <div className="absolute z-50 mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]">
                              {[
                                { key: 'lead', label: 'Lead' },
                                { key: 'contacted', label: 'Contacted' },
                                { key: 'custom_request', label: 'Custom Request' },
                                { key: 'ghost', label: 'Ghost' },
                                { key: 'converted', label: 'Converted' }
                              ].map(option => {
                                const optionBadge = getStatusBadge({ status: option.key });
                                return (
                                  <button
                                    key={option.key}
                                    data-testid={`relations-status-option-${contact.id}-${option.key}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(contact.id, option.key);
                                    }}
                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${
                                      contact.status === option.key ? 'bg-gray-50' : ''
                                    }`}
                                  >
                                    <span className={`w-2 h-2 rounded-full ${
                                      option.key === 'lead' ? 'bg-orange-500' :
                                      option.key === 'contacted' ? 'bg-blue-500' :
                                      option.key === 'custom_request' ? 'bg-sky-500' :
                                      option.key === 'ghost' ? 'bg-gray-400' :
                                      'bg-purple-500'
                                    }`}></span>
                                    {option.label}
                                    {contact.status === option.key && <Check className="w-3 h-3 ml-auto text-blue-600" />}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {/* Email - hidden on mobile */}
                      <td className="px-4 py-4 hidden md:table-cell">
                        {contact.email ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(contact.email, `email-${contact.id}`);
                            }}
                            data-testid={`relations-copy-email-${contact.id}`}
                            className="group flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600"
                            title="Click to copy"
                          >
                            <span className="truncate max-w-[160px]">{contact.email}</span>
                            {copiedItems[`email-${contact.id}`] ? (
                              <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                            )}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      
                      {/* Phone - hidden on mobile/tablet */}
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {contact.phone ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(contact.phone, `phone-${contact.id}`);
                            }}
                            data-testid={`relations-copy-phone-${contact.id}`}
                            className="group flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600"
                            title="Click to copy"
                          >
                            <span>{contact.phone}</span>
                            {copiedItems[`phone-${contact.id}`] ? (
                              <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                            )}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      
                      {/* Bookings */}
                      <td className="px-4 py-4 text-center">
                        <span className={`text-sm font-medium ${contact.bookingCount > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                          {contact.bookingCount}
                        </span>
                      </td>
                      
                      {/* Waivers */}
                      <td className="px-4 py-4 text-center">
                        <span className={`text-sm font-medium ${contact.waiverCount > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                          {contact.waiverCount}
                        </span>
                      </td>
                      
                      {/* Revenue */}
                      <td className="px-4 py-4 text-right">
                        <span className={`text-sm font-medium ${contact.totalSpent > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                          {formatCurrency(contact.totalSpent, contact.currency)}
                        </span>
                      </td>
                      
                      {/* Joined - hidden on smaller screens */}
                      <td className="px-4 py-4 hidden xl:table-cell whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {formattedJoined}
                        </span>
                      </td>
                      
                      {/* Actions - Simple View button */}
                      <td className="px-6 py-4 text-right">
                        <button
                          data-testid={`relations-view-btn-${contact.id || idx}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedContact(contact);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                        >
                          View
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} contacts
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => p - 1)}
                data-testid="relations-page-prev"
                disabled={pagination.page === 1}
                className="p-2 h-9 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      data-testid={`relations-page-${pageNum}`}
                      className={`w-8 h-9 rounded-lg text-sm font-medium transition-colors ${
                        pagination.page === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setPage(p => p + 1)}
                data-testid="relations-page-next"
                disabled={pagination.page === pagination.totalPages}
                className="p-2 h-9 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Detail Panel Modal */}
      {selectedContact && (
        <ContactDetailPanel 
          contact={selectedContact} 
          onClose={() => setSelectedContact(null)}
          onNavigate={onNavigate} 
        />
      )}
    </TabPageLayout>
  );
};

export default RelationsTab;
